"use strict";
/**
 * THE ORDER JOURNEY — a state machine expressed as DATA, not as code.
 *
 * WHY NOT AWS STEP FUNCTIONS. The model is a state machine and the platform
 * already runs Step Functions, but they are the wrong engine for this one:
 *
 *   · A running execution is PINNED to the definition it started with. You
 *     cannot add, remove or modify a step in an execution already in flight —
 *     which is precisely the requirement here ("every step can be modified").
 *   · Standard executions cap at ONE YEAR. These journeys run for several:
 *     a device financed over 36 months, a warranty claim in year three, a
 *     collection two years after that.
 *   · Human tasks that wait weeks (an ISO underwriting, a shipping decision)
 *     do not fit a task token.
 *
 * So the stages live here as an ordered list of definitions, a journey pins a
 * SNAPSHOT of that list, and state is derived on read from evidence the owning
 * services already hold. Adding a stage is a data change.
 *
 * TWO WAYS A STAGE COMPLETES, and the difference is never blurred:
 *   `evidence` — a fact exists somewhere authoritative (an executed envelope,
 *                a VAR sheet, a keyed terminal, a captured invoice). Nobody
 *                asserts it; it is read.
 *   `manual`   — a person did something the system cannot observe (chose an
 *                ISO, decided a shipment was ready). Recorded as "X said so
 *                at T", never dressed up as a fact.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.CUSTOMER_ORIGIN_LABEL = exports.NEW_CUSTOMER_WINDOW_MS = exports.CUSTOMER_ORIGINS = exports.STAGE_AGE_STUCK_MS = exports.STAGE_AGE_WARN_MS = exports.ORDER_STAGES_V1 = exports.STAGE_LANES = exports.STAGE_GATES = exports.STAGE_OWNERS = void 0;
exports.registerCancellationResolver = registerCancellationResolver;
exports.stageAge = stageAge;
exports.customerOrigin = customerOrigin;
exports.resolveJourney = resolveJourney;
exports.dispatchBlockers = dispatchBlockers;
exports.journeyProgress = journeyProgress;
/**
 * Set once by cancellation.ts at module load. An `import` here would be
 * circular — cancellation.ts already imports this module — and a dynamic
 * import cannot be awaited inside a synchronous resolver.
 *
 * WARNING: this relies on cancellation.ts's module-level
 * `registerCancellationResolver(cancellationStageState)` call actually
 * running as a side effect of being imported. If this package's
 * package.json ever gains `"sideEffects": false`, a bundler is legally
 * allowed to tree-shake that call away as "unused" — and every `cx_*` stage
 * would silently go back to reading `todo` while the order is still held.
 * Do not add `sideEffects: false` here without also switching this to an
 * explicit, non-tree-shakeable registration path.
 */
let resolveCancellationStage;
function registerCancellationResolver(fn) {
    resolveCancellationStage = fn;
}
exports.STAGE_OWNERS = ['system', 'rubytill', 'customer', 'iso', 'warehouse'];
/**
 * What a stage can hold up.
 *
 * `billing` is the second member, added by cancellation: while a settlement is
 * being argued the subscription must not take another payment we may be about
 * to refund. The list was left open on purpose and this is the first thing to
 * need something other than a shipment.
 */
exports.STAGE_GATES = ['dispatch', 'billing'];
/**
 * WHICH TRACK a stage belongs to.
 *
 * After the money is verified the order stops being a queue and becomes two
 * jobs running at once: one team gets the merchant account boarded, another
 * picks and configures the hardware. Drawing them in a single column implies
 * an order they do not have, and makes every screen look further behind than
 * it is.
 *
 * They are not fully independent — a card device cannot finish configuring
 * without the TID the merchant lane produces — and the Configured stage says
 * so when it is waiting.
 */
/*
  `merchant` and `fulfilment` are KEPT, not removed. Every journey snapshot
  already written names them, and a lane that disappears from this tuple is a
  breaking change to anything that stored one. Nothing in ORDER_STAGES_V1 uses
  them any more — the four lanes below replaced them — but the values stay.

  The four live lanes fork off `allocated` and run at the same time:
    card             — the merchant account. The one that GROWS: everything a
                       payments partner asks for lands here.
    payment_devices  — the card readers
    pos_devices      — the tills
    accessories      — peripherals and accessories, which need no account and
                       no register, only finishing and counting
*/
exports.STAGE_LANES = [
    'main', 'merchant', 'fulfilment',
    'card', 'payment_devices', 'pos_devices', 'accessories',
];
/**
 * THE PRODUCTION LINE, v1 — order to shipped.
 *
 * Ordered as the work actually happens. Two things are deliberately NOT gates:
 * compliance (RubyTill's own pack, which never blocks a shipment) and payment
 * (signing comes first — the leverage is gone once money has moved).
 */
exports.ORDER_STAGES_V1 = [
    {
        id: 'account', label: 'Account created', owner: 'customer', completion: 'evidence',
        hint: 'Who signed up — business, legal name and the email everything is sent to.',
    },
    {
        id: 'package', label: 'Ruby Till package chosen', owner: 'customer', completion: 'evidence',
        hint: 'The plan they picked and what they put in the order.',
    },
    {
        id: 'placed', label: 'Ordered devices', owner: 'system', completion: 'evidence',
        hint: 'What was ordered, for which store.',
    },
    {
        id: 'agreement', label: 'Agreement signed', owner: 'customer', completion: 'evidence',
        blocks: ['dispatch'], skippableWhen: 'nothing_financed',
        hint: 'Signing comes BEFORE payment: the finance is a credit contract formed at signature.',
    },
    {
        id: 'payment', label: 'Paid', owner: 'customer', completion: 'evidence',
        hint: 'Anything due today. The monthly subscription bills on its own cycle.',
    },
    {
        id: 'payment_verified', label: 'Payment verified', owner: 'rubytill', completion: 'manual',
        hint: 'A person checks the money actually landed — in Stripe, against the schedule and the subscription — before anything is picked. Stripe says "paid"; this says somebody looked.',
    },
    {
        id: 'packages_activated', label: 'Packages activated', owner: 'system', completion: 'evidence',
        hint: 'The last step of the sale — what they paid for becomes what the software turns on. Red here means the customer has been charged and cannot use what they bought.',
    },
    /*
      STOCK ALLOCATED IS THE FORK POINT, and therefore sits on the spine.
  
      It used to live on a `fulfilment` lane running beside the merchant one,
      which said the picking and the merchant account were two independent jobs.
      They are not independent of ALLOCATION: until specific units are reserved,
      none of the four lanes below knows what it is working on — there is no
      device to configure, no reader to key, nothing to couple. So allocation
      finishes on the spine and the four lanes open out of it.
    */
    {
        id: 'allocated', label: 'Stock allocated', owner: 'warehouse', completion: 'evidence',
        hint: 'Specific units reserved against this order. Everything below forks from here.',
    },
    {
        id: 'compliance', label: 'Merchant processing — document verification',
        owner: 'rubytill', completion: 'evidence',
        lane: 'card',
        // NO hint. The pack itself is shown in the stage, as marks — a sentence
        // listing the same five documents by name says nothing the icons do not.
    },
    {
        id: 'merchant_assigned', label: 'Merchant processing selection', owner: 'rubytill',
        // EVIDENCE, not manual — even though a person decides. The assignment is
        // stored on the merchant application with who made it and when, so reading
        // that record is both simpler and more truthful than asking someone to
        // tick a second box saying they did what they already did.
        completion: 'evidence',
        lane: 'card',
        skippableWhen: 'no_payment_devices',
        hint: 'A person decides which ISO suits this sector. Multiple partners, different specialities.',
    },
    {
        /*
          ONE STAGE, not two.
    
          "Terminals keyed" used to be separate. It was removed as a stage, but the
          EVIDENCE could not be: hardware-service refuses packing when a payment
          unit has no TID, and a screen that stopped mentioning it would say
          nothing is blocking while the server blocked. So the sheet and the
          terminals it issues are one step — which is also how the work actually
          happens: the partner uploads the VAR and keys the devices off it.
        */
        /*
          THE ID STAYS `var_sheet`. The label is now 'ISO processing' because the
          stage covers the whole period the application is on the partner's side —
          the sheet, the documents they ask for, and the thread. The id is pinned
          in every journey snapshot already written; renaming it would orphan them.
        */
        id: 'var_sheet', label: 'ISO processing', owner: 'iso', completion: 'evidence',
        lane: 'card',
        skippableWhen: 'no_payment_devices',
        hint: 'The processor issues the sheet; every card device on the order is keyed to a TID off it. A device cannot finish configuring without one.',
    },
    /* ── PAYMENT DEVICES LANE ───────────────────────────────────────────────
       The card readers. Their own checklist first, then into the location's
       device pool against a TPN, then keyed to the TID the card lane produced.
       Three steps rather than one because they fail for three different
       reasons, and "configure payment devices" told you which none of them. */
    {
        id: 'provision_payment', label: 'Configure hardware', owner: 'warehouse',
        completion: 'evidence', lane: 'payment_devices',
        skippableWhen: 'no_payment_devices',
        hint: 'The card devices’ own provisioning plan. A reader is a different piece of work from a till, which is why it is not counted with them.',
    },
    /*
      THE TWO THINGS SOMEBODY DOES ON THE TERMINAL ITSELF, before it can pair.
  
      Both happen in the processor's portal and on the device — nothing here can
      observe either, so they are MANUAL: a person ticks them and the record says
      who and when. They sit BEFORE `register_emv` because that is the step they
      gate; a terminal with no parameters downloaded pairs into a register and
      then fails at the first card, which looks like a pairing fault and is not.
    */
    {
        id: 'emv_setup_config', label: 'Setup configurations', owner: 'rubytill',
        completion: 'manual', lane: 'payment_devices',
        skippableWhen: 'no_payment_devices',
        hint: 'The terminal’s profile set up in the processor’s portal — SPIn mode, tip and receipt options.',
    },
    {
        id: 'emv_download_params', label: 'Download parameters', owner: 'rubytill',
        completion: 'manual', lane: 'payment_devices',
        skippableWhen: 'no_payment_devices',
        hint: 'The terminal pulls that profile down over the network. Until it has, it is not programmed — whatever the portal says.',
    },
    {
        id: 'register_emv', label: 'Register EMV', owner: 'rubytill',
        completion: 'evidence', lane: 'payment_devices',
        skippableWhen: 'no_payment_devices',
        hint: 'Each reader into the location’s device pool against its own TPN — one TPN per device, never shared. Waits on the VAR sheet: registering against a TID nobody was issued is how a reader ends up keyed to the wrong merchant.',
    },
    {
        /*
          THE ID STAYS `configure_payment` — it is pinned in every journey
          snapshot already written. What changed is its SCOPE: the checklist half
          moved to `provision_payment` above, so this stage is now exactly what
          its new label says, the TID keying.
        */
        id: 'configure_payment', label: 'EMV device configuration', owner: 'rubytill',
        completion: 'evidence', lane: 'payment_devices',
        blocks: ['dispatch'],
        skippableWhen: 'no_payment_devices',
        hint: 'Each device keyed to its TID off the VAR sheet, with its processing options set. The end of the payment path.',
    },
    /* ── POS DEVICES LANE ───────────────────────────────────────────────────
       The tills. The register cannot be made before the build is on the box:
       a provisioning code nothing claims simply expires. */
    {
        id: 'provisioned', label: 'Configure hardware & install RubyTill', owner: 'warehouse',
        completion: 'evidence', lane: 'pos_devices',
        skippableWhen: 'no_pos_devices',
        /*
          THE GATE LIVES HERE, not on the VAR sheet and not on packing.
          Putting the gate where the work is means the screen and the warehouse
          agree about what "configured" means, instead of a device reading as
          finished and then being refused at the box.
        */
        blocks: ['dispatch'],
        hint: 'The till’s plan and its build. A STATUS view here; the work itself is done in device management.',
    },
    {
        id: 'create_register', label: 'Create the register', owner: 'rubytill',
        completion: 'evidence', lane: 'pos_devices',
        skippableWhen: 'no_pos_devices',
        hint: 'One register per POS device, and the provisioning code it claims itself with. The order already knows how many are needed and which unit each is for.',
    },
    /* ── ACCESSORIES LANE ───────────────────────────────────────────────────
       Peripherals and accessories. No merchant account, no TPN, no register —
       they only have to be finished and counted. The lane exists so they are
       not forgotten while the other three take the attention. */
    {
        id: 'provision_accessories', label: 'Configure hardware', owner: 'warehouse',
        completion: 'evidence', lane: 'accessories',
        skippableWhen: 'no_accessories',
        hint: 'Whatever the model’s plan says — a printer paired and test-fed, a scanner programmed.',
    },
    {
        id: 'accessories_ready', label: 'Ready to pack', owner: 'warehouse',
        completion: 'manual', lane: 'accessories',
        skippableWhen: 'no_accessories',
        hint: 'Counted and set aside with the rest of the order.',
    },
    /* ── BACK TO ONE PATH ───────────────────────────────────────────────────
       The couple is the merge point: it needs a register from one lane and a
       keyed reader from another, so it cannot exist until both are done. */
    {
        id: 'device_couple', label: 'Couple the register and its payment device',
        owner: 'rubytill', completion: 'evidence',
        blocks: ['dispatch'],
        skippableWhen: 'no_payment_devices',
        hint: 'One POS device to one payment device, per location. Once coupled, scanning either barcode brings up both — so nobody packs half a pair.',
    },
    /*
      HAND THE TILL OVER — the step between "we have proved this works" and
      "seal the box".
  
      Everything above this proved the device works on OUR account. This is where
      it stops being ours: the owner gets a full-access role and a PIN, the
      terminal is deregistered so its session dies with us, and the two codes go
      out by email and on the card in the box.
  
      MANUAL, because a person decides the moment. It sits straight after the
      couple: once a till and its reader are paired, the thing being handed over
      exists as one unit, and everything after this — tenders, layout, the test
      run — is configuration done ON the merchant's own till rather than ours.
    */
    {
        id: 'till_handover', label: 'Hand the till over', owner: 'rubytill', completion: 'manual',
        skippableWhen: 'no_pos_devices',
        hint: 'Give the owner their access and PIN, clear our test session off the device, and send the codes that go in the box.',
    },
    {
        id: 'payment_types', label: 'Payment types', owner: 'rubytill', completion: 'evidence',
        hint: 'What each register may take. Enabling a device-backed method assigns the coupled reader — the couple already answered which device, so it is not asked twice.',
    },
    {
        id: 'button_layout', label: 'Button layout', owner: 'rubytill', completion: 'manual',
        skippableWhen: 'no_pos_devices',
        hint: 'A starting layout per register, so the merchant does not open an empty screen.',
    },
    {
        id: 'packages_verified', label: 'Packages verified against the order',
        owner: 'rubytill', completion: 'manual',
        hint: 'What the order billed against what the software actually turned on. A mismatch is raised for a person — never charged, never silently corrected.',
    },
    /*
      THE TESTS, once every lane is back on one line.
  
      They are end-to-end and need the whole kit together: a test transaction
      proves the reader, the till and the merchant account work AS A SET, which
      is not a thing any one lane could have proved on its own. Manual, because
      somebody physically does them.
    */
    {
        id: 'test_transaction', label: 'Test transaction', owner: 'warehouse', completion: 'manual',
        skippableWhen: 'no_payment_devices',
        hint: 'A real sale on the live merchant account, end to end.',
    },
    {
        id: 'refund_transaction', label: 'Refund transaction', owner: 'warehouse', completion: 'manual',
        skippableWhen: 'no_payment_devices',
        hint: 'The test sale, reversed — the half people forget until a customer needs it.',
    },
    {
        id: 'receipt_printing', label: 'Receipt printing verified', owner: 'warehouse', completion: 'manual',
    },
    {
        id: 'barcode_scanning', label: 'Barcode scanning verified', owner: 'warehouse', completion: 'manual',
    },
    {
        id: 'packed', label: 'Packed', owner: 'warehouse', completion: 'manual',
        hint: 'Boxed and ready to hand over.',
    },
    {
        /* "Shipping and handling", not "Shipped": this stage is the WORK of
           getting the order out — boxing devices, choosing a carrier, recording
           tracking — and only ends in the state its old name described. The
           merchant's own progress view keeps its separate wording
           (merchantOrderProgress: "Your order is about to ship"), so this renames
           the admin rail alone. */
        id: 'shipped', label: 'Shipping and handling', owner: 'warehouse', completion: 'manual',
        hint: 'A human task — boxes, carrier, tracking, and whatever else the shipment needs.',
    },
    /*
      `shipping_verified` WAS HERE — removed 2026-09-15. Shipped now requires a
      tracking number on every box, which is the check it stood for. Journeys
      that already carry it keep it in their own `stageIds` snapshot.
    */
    {
        id: 'delivered', label: 'Delivered', owner: 'system', completion: 'evidence',
    },
    {
        id: 'live', label: 'Live in production', owner: 'system', completion: 'evidence',
        hint: 'Trading at the store. Support, claims and collections attach from here on — this stage never closes.',
    },
];
/**
 * HOW LONG THIS HAS BEEN SOMEBODY'S MOVE — the traffic reading on the pipeline.
 *
 * Derived, never stored: the clock starts at the moment the PREVIOUS stage on
 * the same lane completed, which is a fact that already exists. A stored
 * "stuck since" would be a second copy that drifts the first time a stage is
 * undone, and it is the copy that would be quoted at somebody.
 *
 * The thresholds are here rather than in a component so an order screen, a
 * dashboard tile and a digest email cannot disagree about what "stuck" means.
 */
exports.STAGE_AGE_WARN_MS = 2 * 24 * 60 * 60 * 1000;
exports.STAGE_AGE_STUCK_MS = 3 * 24 * 60 * 60 * 1000;
function stageAge(activeSince, now = Date.now()) {
    if (!activeSince)
        return undefined;
    const held = now - activeSince;
    if (held >= exports.STAGE_AGE_STUCK_MS)
        return 'stuck';
    if (held >= exports.STAGE_AGE_WARN_MS)
        return 'warn';
    return 'ok';
}
exports.CUSTOMER_ORIGINS = ['new_business', 'new_location', 'existing_business', 'unknown'];
/**
 * Which of the three ways this order arrived.
 *
 * Derived rather than stored, because every input is already a fact somebody
 * else owns — when the tenant signed up, when this store was created, how many
 * stores they have. A stored flag would be a fourth copy to keep in step, and
 * it is the copy that goes stale.
 *
 * `NEW_CUSTOMER_WINDOW_MS` is the only judgement here: a store created within
 * a fortnight of the business itself is that business opening, not a second
 * store. Longer and a genuine second store looks like a first one; shorter and
 * a slow onboarding gets miscounted.
 */
exports.NEW_CUSTOMER_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;
function customerOrigin(ctx) {
    const o = ctx.origin;
    if (!o)
        return 'unknown';
    const { tenantCreatedAt, locationCreatedAt, locationCount } = o;
    if (typeof locationCount === 'number' && locationCount > 1) {
        // More than one store. Is THIS one newly opened, or is an established
        // store simply buying more equipment?
        if (locationCreatedAt && Date.now() - locationCreatedAt < exports.NEW_CUSTOMER_WINDOW_MS)
            return 'new_location';
        return 'existing_business';
    }
    if (tenantCreatedAt && locationCreatedAt) {
        return Math.abs(locationCreatedAt - tenantCreatedAt) < exports.NEW_CUSTOMER_WINDOW_MS
            ? 'new_business'
            : 'new_location';
    }
    if (tenantCreatedAt && Date.now() - tenantCreatedAt < exports.NEW_CUSTOMER_WINDOW_MS)
        return 'new_business';
    if (typeof locationCount === 'number' && locationCount === 1)
        return 'existing_business';
    return 'unknown';
}
exports.CUSTOMER_ORIGIN_LABEL = {
    new_business: 'New business',
    new_location: 'Existing customer · new location',
    existing_business: 'Existing customer',
    unknown: 'Origin unknown',
};
const UNIT_REACHED = {
    in_stock: 0, awaiting: 0, allocated: 1, processing: 1, provisioning: 2, configured: 3,
    packing: 4, shipped: 5, delivered: 6, deployed: 7,
};
/*
  A WRITTEN-OFF UNIT NO LONGER HOLDS A STAGE. `lost`/`returned`/`rma`/`retired`
  rank −1, so one lost reader left `provision_payment` at 4/5 forever with every
  live reader configured. Same rule as `deriveOrderStatus`: dead units drop out
  only while a live unit remains — an all-dead lane is still not done.
*/
const DEAD_UNIT_STATES = new Set(['returned', 'rma', 'retired', 'lost']);
const reached = (units, atLeast) => {
    const live = (units || []).filter((u) => !DEAD_UNIT_STATES.has(u.state));
    return live.length > 0 && live.every((u) => (UNIT_REACHED[u.state] ?? -1) >= atLeast);
};
/** When the LAST unit got there — the moment the stage actually completed. */
const reachedAt = (units) => {
    const t = Math.max(0, ...(units || []).map((u) => u.updatedAt || 0));
    return t || undefined;
};
/**
 * Every stage's state, right now, from the facts.
 *
 * ORDER MATTERS but does not gate: a later stage can be complete while an
 * earlier one is not, and the screen must say so rather than hiding it.
 * Warehouses allocate stock before underwriting finishes all the time, and
 * pretending otherwise makes the timeline lie.
 *
 * `active` is the FIRST incomplete stage — the one to work on. Every other
 * incomplete stage is `todo`, except one that is holding up dispatch, which
 * reads `blocked` wherever it sits.
 */
function resolveJourney(stages, ctx) {
    const paymentUnits = ctx.paymentUnitIds || [];
    /*
      A READER IS SETTLED when a sheet is bound to it, or when a TID off that
      sheet has been keyed to it. Same rule as `dispatchReadiness`, deliberately:
      a rail that says "done" while the packing gate refuses, or the reverse, is
      a rail nobody can act on.
  
      The bound sheet is what actually arrives — the merchant's own or the
      partner's. Demanding the TID as well left the card lane unfinishable on a
      bring-your-own-processor order, where no ISO is assigned and the only route
      that could key a terminal requires one.
    */
    const keyed = new Set([
        ...(ctx.merchant?.terminalAssignments || []).map((t) => t.unitId),
        ...(ctx.merchant?.varSheets || [])
            .filter((v) => v.unitId && v.supersededAt === undefined)
            .map((v) => v.unitId),
    ]);
    const docs = ctx.compliance?.documents || [];
    const requiredDocs = docs.filter((d) => d.required !== false);
    const verifiedDocs = requiredDocs.filter((d) => d.status === 'verified' || d.status === 'accepted');
    const expectedPayment = ctx.expectedPaymentDeviceCount ?? paymentUnits.length;
    const skip = (s) => {
        // Skipped only when the ORDER calls for no card device at all — not merely
        // because none has been allocated yet.
        if (s.skippableWhen === 'no_payment_devices')
            return expectedPayment === 0 && paymentUnits.length === 0;
        if (s.skippableWhen === 'brought_own_processor')
            return ctx.merchant?.merchantSource === 'existing';
        if (s.skippableWhen === 'nothing_financed')
            return !ctx.envelopeId;
        /*
          UNANSWERED IS NOT ZERO. A caller that supplies no split leaves these
          undefined, and skipping the lane then would claim "no tills on this
          order" about an order nobody has told us the shape of. Only an
          explicitly empty list skips.
        */
        if (s.skippableWhen === 'no_pos_devices')
            return ctx.posUnitIds?.length === 0;
        if (s.skippableWhen === 'no_accessories')
            return ctx.accessoryUnitIds?.length === 0;
        return false;
    };
    const rows = stages.map((def) => {
        const blocksDispatch = !!def.blocks?.includes('dispatch');
        /*
          COMPLETED BY HAND, OVER THE EVIDENCE.
    
          Some stages never get their evidence on a real order — no envelope was
          ever sent, a reader was keyed in a portal we cannot read — and a stage
          that can only wait leaves the order unfinishable. A person may complete
          it, with a reason, and it says so: "Completed manually by …", never the
          evidence's own wording. Checked before the skip rule too, so a stage the
          resolver skipped can still be acknowledged. Undo restores the evidence.
        */
        const forced = ctx.manual?.[def.id];
        if (forced?.override && def.completion !== 'manual') {
            return {
                def, state: 'done', blocksDispatch, at: forced.at, evidence: 'manual override', assertedBy: forced,
                detail: `Completed manually by ${forced.name}${forced.note ? ` — ${forced.note}` : ''}.`,
            };
        }
        if (skip(def)) {
            return {
                def, state: 'skipped', blocksDispatch: false,
                detail: def.skippableWhen === 'no_payment_devices' ? 'No card devices on this order.'
                    : def.skippableWhen === 'brought_own_processor' ? 'They brought their own processor.'
                        : def.skippableWhen === 'no_pos_devices' ? 'No POS devices on this order.'
                            : def.skippableWhen === 'no_accessories' ? 'No peripherals or accessories on this order.'
                                : 'Nothing on this order is financed.',
            };
        }
        const manual = ctx.manual?.[def.id];
        if (def.completion === 'manual') {
            if (manual) {
                return { def, state: 'done', blocksDispatch, at: manual.at, detail: `Marked done by ${manual.name}.`, assertedBy: manual };
            }
            /*
              A MANUAL STAGE WHOSE WORK LEAVES A RECORD completes from that record, so
              nobody has to tick what the screen already watched happen. Still
              `manual`: a person can still tick it, and the tick wins.
            */
            if (def.id === 'button_layout') {
                const posIds = ctx.posUnitIds || [];
                const regs = ctx.registers || [];
                const setUp = regs.filter((r) => typeof r.posSetupAt === 'number');
                if (posIds.length > 0 && regs.length >= posIds.length && setUp.length === regs.length) {
                    return { def, state: 'done', blocksDispatch, evidence: 'set up pos',
                        at: Math.max(...setUp.map((r) => r.posSetupAt)),
                        detail: `${regs.length} register${regs.length === 1 ? '' : 's'} set up.` };
                }
                if (setUp.length > 0) {
                    return { def, state: 'todo', blocksDispatch,
                        detail: `${setUp.length} of ${Math.max(regs.length, posIds.length)} registers set up.` };
                }
            }
            if (def.id === 'receipt_printing') {
                // Both test receipts photographed = the printer demonstrably printed twice.
                const test = ctx.manual?.test_transaction?.attachment ? ctx.manual.test_transaction : undefined;
                const refund = ctx.manual?.refund_transaction?.attachment ? ctx.manual.refund_transaction : undefined;
                if (test && refund) {
                    return { def, state: 'done', blocksDispatch, evidence: 'receipt photos',
                        at: Math.max(test.at, refund.at), detail: 'Both test receipts photographed.' };
                }
                return { def, state: 'todo', blocksDispatch,
                    detail: `${(test ? 1 : 0) + (refund ? 1 : 0)} of 2 receipts uploaded.` };
            }
            if (def.id === 'till_handover' && ctx.tillHandover?.at) {
                return { def, state: 'done', blocksDispatch, evidence: 'pos handover',
                    at: ctx.tillHandover.at, detail: 'Handed over — the owner has their access and PIN.' };
            }
            return { def, state: 'todo', blocksDispatch, detail: 'Waiting on a person.' };
        }
        // Cancellation stages resolve from the cancellation record. Kept in
        // cancellation.ts so this switch stays about the order.
        const cxRow = resolveCancellationStage?.(def, ctx);
        if (cxRow)
            return cxRow;
        switch (def.id) {
            case 'account': {
                const t = ctx.tenant;
                const named = t?.businessName || t?.legalName;
                const origin = customerOrigin(ctx);
                const how = origin === 'unknown' ? '' : `${exports.CUSTOMER_ORIGIN_LABEL[origin]} · `;
                const nth = ctx.origin?.locationCount && ctx.origin.locationCount > 1
                    ? ` · store ${ctx.origin.locationCount}`
                    : '';
                return {
                    def,
                    state: named ? 'done' : 'todo',
                    blocksDispatch,
                    at: t?.createdAt,
                    evidence: 'tenant + location records',
                    detail: named
                        ? `${how}${named}${t?.ownerEmail ? ` · ${t.ownerEmail}` : ''}${nth}`
                        : 'No account details on file.',
                };
            }
            case 'package': {
                const names = ctx.plan?.packageNames || [];
                const items = (ctx.order?.items || []).length;
                if (names.length === 0 && items === 0)
                    return { def, state: 'todo', blocksDispatch, detail: 'Nothing chosen yet.' };
                return { def, state: 'done', blocksDispatch, at: ctx.order?.createdAt, evidence: 'plan + order lines',
                    detail: names.length ? names.join(' · ') : `${items} line${items === 1 ? '' : 's'} on the order.` };
            }
            case 'placed': {
                const n = (ctx.order?.items || []).reduce((s, i) => s + (i.quantity || 1), 0);
                return { def, state: ctx.order ? 'done' : 'todo', blocksDispatch, at: ctx.order?.createdAt, evidence: 'order record',
                    detail: ctx.order ? `${n} device${n === 1 ? '' : 's'} ordered.` : 'No order.' };
            }
            case 'compliance': {
                if (requiredDocs.length === 0)
                    return { def, state: 'todo', blocksDispatch, detail: 'No documents requested yet.' };
                const done = verifiedDocs.length === requiredDocs.length;
                const lastDoc = Math.max(0, ...requiredDocs.map((d) => d.uploadedAt || 0));
                return { def, state: done ? 'done' : 'todo', blocksDispatch,
                    at: done && lastDoc ? lastDoc : undefined, evidence: 'location request',
                    detail: `${verifiedDocs.length} of ${requiredDocs.length} verified.` };
            }
            case 'agreement': {
                const executed = ctx.envelopeStatus === 'executed';
                return { def, state: executed ? 'done' : 'todo', blocksDispatch,
                    at: executed ? ctx.envelopeExecutedAt : undefined, evidence: 'documents-service',
                    detail: executed ? 'Signed.' : `Envelope is "${ctx.envelopeStatus || 'not sent'}".` };
            }
            case 'payment': {
                if (ctx.payment?.paid)
                    return { def, state: 'done', blocksDispatch, at: ctx.payment.paidAt, evidence: 'billing', detail: 'Paid.' };
                return { def, state: 'todo', blocksDispatch, detail: ctx.payment?.dueCents ? 'Due today, not yet collected.' : 'Nothing collected yet.' };
            }
            case 'packages_activated': {
                const a = ctx.packageActivation;
                if (!a)
                    return { def, state: 'todo', blocksDispatch, detail: 'Nothing activated yet.' };
                if (a.status === 'ok') {
                    return {
                        def, state: 'done', blocksDispatch, at: a.at, evidence: 'activate_packages',
                        detail: a.packages?.length ? `${a.packages.length} package(s) enabled.` : 'Packages enabled.',
                    };
                }
                return {
                    def, state: 'blocked', blocksDispatch, at: a.at, evidence: 'activate_packages',
                    detail: a.failedSteps?.length
                        ? `Activation ${a.status} — failed: ${a.failedSteps.join(', ')}.`
                        : `Activation ${a.status}.`,
                };
            }
            case 'merchant_assigned': {
                const m = ctx.merchant;
                // An assigned partner wins over the original checkout answer here too —
                // same precedence as `var_sheet` below. Once a partner has been given a
                // BYO application (the rescue path for a stalled own-processor record),
                // this stage's job is to name that partner, not to repeat what the
                // merchant said months ago.
                if (m?.merchantSource === 'existing' && !m?.assignedIsoId) {
                    return { def, state: 'done', blocksDispatch, detail: 'They brought their own processor.' };
                }
                return m?.assignedIsoId
                    ? { def, state: 'done', blocksDispatch, at: m.assignedAt, evidence: 'merchant application', detail: `With ${m.assignedIsoName || 'a partner'}.` }
                    : { def, state: 'todo', blocksDispatch, detail: 'Nobody has been given this yet.' };
            }
            case 'underwriting': {
                const s = ctx.merchant?.status;
                if (s === 'approved')
                    return { def, state: 'done', blocksDispatch, at: ctx.merchant?.approvedAt, evidence: 'merchant application', detail: 'Approved.' };
                if (s === 'declined')
                    return { def, state: 'blocked', blocksDispatch, detail: 'Declined by the processor.' };
                return { def, state: 'todo', blocksDispatch, detail: s === 'submitted' ? 'At the processor.' : s === 'info_requested' ? 'They asked the merchant for something.' : 'Not submitted yet.' };
            }
            case 'var_sheet': {
                /*
                  AN ASSIGNED PARTNER WINS OVER THE ORIGINAL ANSWER.
        
                  `merchantSource` records what the merchant said at checkout and is
                  never rewritten. Once a partner has been assigned — which is how a
                  stalled own-processor application is rescued — the partner is the one
                  producing the sheet, whatever the merchant answered months ago. Read
                  the work, not the intention.
                */
                const byo = ctx.merchant?.merchantSource === 'existing' && !ctx.merchant?.assignedIsoId;
                const voice = byo
                    ? {
                        labelOverride: "Merchant's own VAR sheets",
                        ownerOverride: 'customer',
                        hintOverride: 'The merchant processes cards already and sends the sheets themselves. Every card device on the order is keyed to a TID off them. A device cannot finish configuring without one.',
                    }
                    : {};
                const sheets = ctx.merchant?.varSheets || [];
                const nSheets = sheets.length;
                const lastSheet = nSheets > 0 ? Math.max(...sheets.map((v) => v.uploadedAt || 0)) || undefined : undefined;
                if (nSheets === 0) {
                    return { def, state: 'todo', blocksDispatch, evidence: 'merchant application', ...voice,
                        detail: `No sheet yet — ${expectedPayment} card device${expectedPayment === 1 ? '' : 's'} on this order ${expectedPayment === 1 ? 'needs' : 'need'} one.` };
                }
                // Nothing allocated yet: the devices are known from the order, but
                // there is no unit to key a TID to. That is "not yet", not "blocked" —
                // the warehouse has to allocate before anyone can key anything.
                if (paymentUnits.length === 0) {
                    return { def, state: 'todo', blocksDispatch, at: lastSheet, evidence: 'merchant application', ...voice,
                        detail: `${nSheets} sheet${nSheets === 1 ? '' : 's'} in — allocate stock, then each device is keyed off ${nSheets === 1 ? 'it' : 'them'}.` };
                }
                const n = paymentUnits.filter((u) => keyed.has(u)).length;
                const done = n === paymentUnits.length;
                const lastKey = Math.max(0, ...(ctx.merchant?.terminalAssignments || []).map((t) => t.assignedAt || 0));
                return { def, state: done ? 'done' : 'blocked', blocksDispatch,
                    at: done ? (lastKey || lastSheet) : undefined, evidence: 'merchant application', ...voice,
                    detail: `${nSheets} sheet${nSheets === 1 ? '' : 's'} in · ${n} of ${paymentUnits.length} device${paymentUnits.length === 1 ? '' : 's'} keyed.` };
            }
            case 'allocated': {
                const have = (ctx.units || []).length;
                const need = ctx.neededUnitCount ?? have;
                const allocDone = have > 0 && have >= need;
                return { def, state: allocDone ? 'done' : 'todo', blocksDispatch,
                    at: allocDone ? reachedAt(ctx.units) : undefined, evidence: 'unit records',
                    detail: need ? `${have} of ${need} units allocated.` : `${have} allocated.` };
            }
            /* ── PAYMENT DEVICES LANE ─────────────────────────────────────────── */
            /* Their own checklist. NOT the TID — that is `configure_payment`. */
            case 'provision_payment': {
                const mine = (ctx.units || []).filter((u) => paymentUnits.includes(u.unitId));
                if (mine.length === 0) {
                    return { def, state: 'todo', blocksDispatch,
                        detail: `${expectedPayment} card device${expectedPayment === 1 ? '' : 's'} to configure — allocate stock first.` };
                }
                const stepsDone = reached(mine, 3);
                return { def, state: stepsDone ? 'done' : 'todo', blocksDispatch,
                    at: stepsDone ? reachedAt(mine) : undefined, evidence: 'unit states',
                    detail: stepsDone
                        ? `${mine.length} card device${mine.length === 1 ? '' : 's'} configured.`
                        : `Not all ${mine.length} card device${mine.length === 1 ? '' : 's'} configured.` };
            }
            /*
              INTO THE POOL, against a TPN.
      
              Blocked rather than todo while the VAR sheet is outstanding, because
              that is a wait on ANOTHER LANE and the screen exists to say which lane
              is holding which up.
            */
            case 'register_emv': {
                const registered = new Set(ctx.emvRegisteredUnitIds || []);
                const varSheetIn = !!ctx.merchant?.varSheets?.length;
                /* Same rule as keying: a device can only be put in the pool once a
                   unit is actually reserved for it. */
                const mine = (ctx.units || [])
                    .filter((u) => paymentUnits.includes(u.unitId))
                    .map((u) => u.unitId);
                if (mine.length === 0) {
                    return { def, state: 'todo', blocksDispatch, detail: 'No card devices allocated yet.' };
                }
                const missing = mine.filter((u) => !registered.has(u));
                if (missing.length === 0) {
                    return { def, state: 'done', blocksDispatch, evidence: 'location device pool',
                        detail: `${mine.length} device${mine.length === 1 ? '' : 's'} in the pool, each with its own TPN.` };
                }
                if (!varSheetIn) {
                    return { def, state: 'blocked', blocksDispatch, evidence: 'merchant application',
                        detail: `Waiting on the VAR sheet — ${missing.length} device${missing.length === 1 ? '' : 's'} cannot be registered without a TID.` };
                }
                return { def, state: 'todo', blocksDispatch,
                    detail: `${missing.length} of ${mine.length} still to register.` };
            }
            /* THE TID, and only the TID. The checklist half is `provision_payment`. */
            case 'configure_payment': {
                /* ALLOCATED payment units, not merely expected ones. `paymentUnitIds`
                   can name devices no unit has been reserved for yet, and keying is a
                   question about a device in someone's hands. */
                const mine = (ctx.units || [])
                    .filter((u) => paymentUnits.includes(u.unitId))
                    .map((u) => u.unitId);
                if (mine.length === 0) {
                    return { def, state: 'todo', blocksDispatch,
                        detail: `${expectedPayment} card device${expectedPayment === 1 ? '' : 's'} to key — allocate stock first.` };
                }
                const unkeyed = mine.filter((u) => !keyed.has(u));
                if (unkeyed.length > 0) {
                    return { def, state: 'blocked', blocksDispatch, evidence: 'merchant application',
                        detail: `${unkeyed.length} of ${mine.length} still ${unkeyed.length === 1 ? 'needs' : 'need'} a TID.` };
                }
                /*
                  KEYED IS NOT CONFIGURED, and this stage used to stop at keyed.
        
                  A TID says the reader belongs to the right merchant. It says nothing
                  about whether anyone installed the app on it, built its profile in the
                  processor's portal, or made it pull that profile down — and a terminal
                  missing any of the three keys perfectly well, ships, and fails at the
                  first card. Since this is the stage that blocks dispatch, it is the
                  right place to hold that.
        
                  `undefined` means the caller does not track the steps, and resolves as
                  it always did. An empty array means it does and none are done.
                */
                if (ctx.emvConfiguredUnitIds) {
                    const unconfigured = mine.filter((u) => !ctx.emvConfiguredUnitIds.includes(u));
                    if (unconfigured.length > 0) {
                        return { def, state: 'todo', blocksDispatch, evidence: 'device pool',
                            detail: `${unconfigured.length} of ${mine.length} still ${unconfigured.length === 1 ? 'needs' : 'need'} the app installed and parameters downloaded.` };
                    }
                }
                return { def, state: 'done', blocksDispatch, evidence: 'merchant application',
                    detail: `${mine.length} card device${mine.length === 1 ? '' : 's'} keyed and configured.` };
            }
            /* ── POS DEVICES LANE ─────────────────────────────────────────────── */
            case 'provisioned': {
                /* Falls back to the old "everything that is not a card device" only
                   when no POS split was given — never silently counts accessories as
                   tills once the caller does supply one. */
                const ids = ctx.posUnitIds
                    ?? ctx.hardwareUnitIds
                    ?? (ctx.units || []).filter((u) => !paymentUnits.includes(u.unitId)).map((u) => u.unitId);
                const mine = (ctx.units || []).filter((u) => ids.includes(u.unitId));
                if (mine.length === 0) {
                    return { def, state: 'todo', blocksDispatch, detail: 'Nothing allocated to configure yet.' };
                }
                const stepsDone = reached(mine, 3);
                return { def, state: stepsDone ? 'done' : 'todo', blocksDispatch,
                    at: stepsDone ? reachedAt(mine) : undefined, evidence: 'unit states',
                    detail: stepsDone
                        ? `${mine.length} POS device${mine.length === 1 ? '' : 's'} configured.`
                        : `Not all ${mine.length} POS device${mine.length === 1 ? '' : 's'} configured.` };
            }
            case 'create_register': {
                const ids = ctx.posUnitIds || [];
                if (ids.length === 0) {
                    return { def, state: 'todo', blocksDispatch, detail: 'No POS devices allocated yet.' };
                }
                const withRegister = new Set((ctx.registers || []).map((r) => r.unitId).filter(Boolean));
                const missing = ids.filter((u) => !withRegister.has(u));
                if (missing.length === 0) {
                    return { def, state: 'done', blocksDispatch, evidence: 'pos registers',
                        detail: `${ids.length} register${ids.length === 1 ? '' : 's'} created.` };
                }
                return { def, state: 'todo', blocksDispatch,
                    detail: `${missing.length} of ${ids.length} still needs a register.` };
            }
            /* ── ACCESSORIES LANE ─────────────────────────────────────────────── */
            case 'provision_accessories': {
                const ids = ctx.accessoryUnitIds || [];
                const mine = (ctx.units || []).filter((u) => ids.includes(u.unitId));
                if (mine.length === 0) {
                    return { def, state: 'todo', blocksDispatch, detail: 'Nothing allocated to configure yet.' };
                }
                const stepsDone = reached(mine, 3);
                return { def, state: stepsDone ? 'done' : 'todo', blocksDispatch,
                    at: stepsDone ? reachedAt(mine) : undefined, evidence: 'unit states',
                    detail: stepsDone
                        ? `${mine.length} item${mine.length === 1 ? '' : 's'} configured.`
                        : `Not all ${mine.length} item${mine.length === 1 ? '' : 's'} configured.` };
            }
            /* ── THE MERGE ────────────────────────────────────────────────────── */
            /*
              ONE POS DEVICE TO ONE PAYMENT DEVICE.
      
              Counted against the POS units, not the pairs: a pair list can be
              complete and still leave a till uncoupled, and it is the uncoupled
              till that ships on its own.
            */
            case 'device_couple': {
                const posIds = ctx.posUnitIds || [];
                const pairs = ctx.couples || [];
                if (posIds.length === 0) {
                    return { def, state: 'todo', blocksDispatch, detail: 'Nothing to couple yet.' };
                }
                const coupled = new Set(pairs.map((p) => p.posUnitId));
                const missing = posIds.filter((u) => !coupled.has(u));
                if (missing.length === 0) {
                    return { def, state: 'done', blocksDispatch, evidence: 'unit links',
                        detail: `${pairs.length} pair${pairs.length === 1 ? '' : 's'} linked.` };
                }
                return { def, state: 'todo', blocksDispatch,
                    detail: `${missing.length} of ${posIds.length} POS device${posIds.length === 1 ? '' : 's'} not coupled.` };
            }
            case 'payment_types': {
                const regs = ctx.registers || [];
                if (regs.length === 0) {
                    return { def, state: 'todo', blocksDispatch, detail: 'No registers yet.' };
                }
                const map = ctx.paymentTypesByTerminal || {};
                const bare = regs.filter((r) => !(map[r.terminalId]?.length));
                if (bare.length === 0) {
                    return { def, state: 'done', blocksDispatch, evidence: 'register settings',
                        detail: `${regs.length} register${regs.length === 1 ? '' : 's'} set up.` };
                }
                return { def, state: 'todo', blocksDispatch,
                    detail: `${bare.length} of ${regs.length} register${regs.length === 1 ? '' : 's'} takes nothing yet.` };
            }
            /*
              VERIFIED, AND SOMEBODY TOLD.
      
              A mismatch that has been found but not raised is worse than one nobody
              looked for: the screen would read green while the merchant is paying
              for something they cannot use. So a mismatch is `blocked` until
              `raisedAt`, never `done`.
            */
            case 'packages_verified': {
                const v = ctx.packageVerification;
                if (!v?.at) {
                    return { def, state: 'todo', blocksDispatch, detail: 'Not checked yet.' };
                }
                const bad = v.mismatchCount ?? 0;
                if (bad === 0) {
                    return { def, state: 'done', blocksDispatch, at: v.at, evidence: 'entitlements vs order',
                        detail: 'Everything billed is switched on.' };
                }
                if (!v.raisedAt) {
                    return { def, state: 'blocked', blocksDispatch, at: v.at, evidence: 'entitlements vs order',
                        detail: `${bad} package${bad === 1 ? '' : 's'} billed and not enabled — raise it for a person.` };
                }
                return { def, state: 'done', blocksDispatch, at: v.raisedAt, evidence: 'entitlements vs order',
                    detail: `${bad} mismatch${bad === 1 ? '' : 'es'} raised for review.` };
            }
            case 'delivered':
                return { def, state: reached(ctx.units, 6) ? 'done' : 'todo', blocksDispatch,
                    at: reached(ctx.units, 6) ? reachedAt(ctx.units) : undefined, evidence: 'unit states',
                    detail: reached(ctx.units, 6) ? 'Delivered.' : 'Not delivered.' };
            case 'live':
                return { def, state: reached(ctx.units, 7) ? 'done' : 'todo', blocksDispatch,
                    at: reached(ctx.units, 7) ? reachedAt(ctx.units) : undefined, evidence: 'unit states',
                    detail: reached(ctx.units, 7) ? 'Trading at the store.' : 'Not live yet.' };
            default:
                return { def, state: 'todo', blocksDispatch, detail: '' };
        }
    });
    /*
      ONE ACTIVE STAGE PER LANE, not one per order.
  
      While the lanes run the warehouse and the partner are both mid-job, and
      marking only the earliest of the two "active" left the other lane reading
      as though nobody had started it — on the screen whose whole purpose is
      comparing the two. Each lane advances on its own.
  
      The clock for a lane's active stage starts when the previous stage ON THAT
      LANE finished; a lane that has not begun inherits the moment the spine
      handed over to it, and failing that the order's own creation.
    */
    const laneOf = (r) => r.def.lane || 'main';
    const started = new Map();
    let handover = ctx.order?.createdAt;
    for (const r of rows) {
        const lane = laneOf(r);
        if (r.state === 'done' && r.at) {
            started.set(lane, r.at);
            // the spine's progress is what a lane inherits when it first opens
            if (lane === 'main')
                handover = r.at;
        }
    }
    const seen = new Set();
    for (const r of rows) {
        if (r.state !== 'todo' && r.state !== 'blocked')
            continue;
        const lane = laneOf(r);
        if (seen.has(lane))
            continue; // only the FIRST open stage in each lane
        seen.add(lane);
        if (r.state === 'todo')
            r.state = 'active';
        r.activeSince = started.get(lane) ?? handover;
        r.age = stageAge(r.activeSince);
    }
    return rows;
}
/**
 * Why dispatch is refused, or nothing.
 *
 * THE ONE QUESTION packing asks. Whatever the stage list happens to contain,
 * the check is the same — which is what lets a new blocker be added as data.
 */
function dispatchBlockers(resolved) {
    return resolved.filter((r) => r.blocksDispatch && r.state !== 'done' && r.state !== 'skipped');
}
/** How far along, for a progress bar. Skipped stages do not count against you. */
function journeyProgress(resolved) {
    const counted = resolved.filter((r) => r.state !== 'skipped');
    return { done: counted.filter((r) => r.state === 'done').length, total: counted.length };
}
//# sourceMappingURL=orderJourney.js.map